import { ColumnDef } from '@tanstack/react-table';
import BenefitTypeCell from '@/components/benefits/BenefitTypeCell';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Translator } from '@/types/translator.type';
import type { HotelAssociatedBenefitItem } from '@/api/benefit.api';
import type { AssociatedBenefitFrequency } from '@/types/benefits/associated-benefit-frequency';

export type AssociatedBenefitRow = {
  id: string;
  index: number;
  benefitId: string;
  frequency: AssociatedBenefitFrequency;
  benefit?: {
    id?: string;
    name?: string | null;
    code?: number | string | null;
    type?: string;
    country?: string | null;
    supplier?: { id: string; name: string | null } | null;
    activity?: { activityType: string; city: string | null } | null;
  };
};

export const HOTEL_ASSOCIATED_BENEFIT_LIST_COLUMNS = (
  t: Translator,
): ColumnDef<AssociatedBenefitRow>[] => [
  {
    id: 'code',
    header: t('benefits.columns.code'),
    cell: ({ row }) =>
      row.original.benefit?.code != null
        ? `P${String(row.original.benefit.code).padStart(4, '0')}`
        : '—',
    enableSorting: false,
  },
  {
    id: 'name',
    header: t('benefits.columns.name'),
    cell: ({ row }) => row.original.benefit?.name ?? '—',
    enableSorting: false,
  },
  {
    id: 'type',
    header: t('hotels.associatedBenefits.type'),
    cell: ({ row }) => (
      <BenefitTypeCell
        type={
          row.original.benefit?.activity?.activityType ??
          row.original.benefit?.type ??
          ''
        }
      />
    ),
    enableSorting: false,
  },
  {
    id: 'supplier',
    header: t('hotels.supplier'),
    cell: ({ row }) => {
      const name = row.original.benefit?.supplier?.name;
      if (name == null || name === '') return '—';
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="max-w-72 xl:max-w-96 inline-block overflow-hidden whitespace-nowrap text-ellipsis">
              {name}
            </TooltipTrigger>
            <TooltipContent>{name}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    },
    enableSorting: false,
  },
  {
    id: 'country',
    header: t('hotels.country'),
    cell: ({ row }) => row.original.benefit?.country ?? '—',
    enableSorting: false,
  },
  {
    id: 'city',
    header: t('hotels.city'),
    cell: ({ row }) => row.original.benefit?.activity?.city ?? '-',
    enableSorting: false,
  },
  {
    id: 'frequency',
    header: t('hotels.associatedBenefits.frequency'),
    cell: ({ row }) =>
      row.original.frequency === 'ONCE'
        ? t('hotels.associatedBenefits.frequencyOnce')
        : t('hotels.associatedBenefits.frequencyEachNight'),
    enableSorting: false,
  },
];

export const HOTEL_ASSOCIATED_BENEFIT_SELECTION_COLUMNS = (
  t: Translator,
): ColumnDef<HotelAssociatedBenefitItem>[] => [
  {
    id: 'code',
    header: t('benefits.columns.code'),
    cell: ({ row }) => {
      const d = row.original;
      return d.code != null ? `P${String(d.code).padStart(4, '0')}` : '—';
    },
    enableSorting: false,
  },
  {
    id: 'name',
    header: t('benefits.columns.name'),
    cell: ({ row }) => row.original.name ?? '—',
    enableSorting: false,
  },
  {
    id: 'type',
    header: t('hotels.associatedBenefits.type'),
    cell: ({ row }) => {
      const typeVal =
        row.original.activity?.activityType ?? row.original.type ?? '';
      return <BenefitTypeCell type={typeVal} />;
    },
    enableSorting: false,
  },
  {
    id: 'supplier',
    header: t('hotels.supplier'),
    cell: ({ row }) => {
      const name = row.original.supplier?.name;
      if (name == null || name === '') return '—';
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="max-w-72 xl:max-w-96 inline-block overflow-hidden whitespace-nowrap text-ellipsis">
              {name}
            </TooltipTrigger>
            <TooltipContent>{name}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    },
    enableSorting: false,
  },
  {
    id: 'country',
    header: t('hotels.country'),
    cell: ({ row }) => row.original.country ?? '—',
    enableSorting: false,
  },
  {
    id: 'city',
    header: t('hotels.city'),
    cell: ({ row }) => row.original.activity?.city ?? '—',
    enableSorting: false,
  },
];
